import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useMemo,
} from 'react'
import {
  findSketchElementFromLayerPathHash,
  findSketchElementFromUuid,
  isCanvasEntityElement,
  isPageElement,
  isSectionElement,
  SketchElement,
} from '@sketch/scene-model'
import { noop } from '@sketch/utils'
import type { CanvasEntityKind } from '../../hooks/types'

/** What a canvas selection is: one of the two entity kinds, or a layer inside one. */
export type CanvasSelectionKind = CanvasEntityKind | 'layer'

/**
 * What is selected on the canvas.
 *
 * Identity only - no scene element. The element is a node in a tree that exists
 * only while the Inspect tab is open and is rebuilt from scratch every time a
 * fragment loads, so anything holding one across either of those events is
 * holding a stale object. Whoever has the current tree resolves this against it
 * instead, with `findSelectedSketchElement`.
 *
 * `uuid` addresses an entity: entities are top-level, so it is enough to find
 * one among them. That is not a guarantee of uniqueness - neither the Mac app
 * nor the backend enforce unique ids, so a file edited through the Sketch API
 * can carry duplicates.
 *
 * For a layer it is not enough even in a well-formed file: two instances of the
 * same symbol contain layers sharing an `elementUUID` (see
 * `SketchElement.elementUUID`), so a layer selection is addressed by its
 * `layerPathHash` - what that field exists for, and what `?layerPath=` links
 * already use.
 */
export type CanvasSelection = {
  kind: CanvasSelectionKind
  uuid: string
  /** Used to identify layers (nodes) in the sketch tree, as `uuid` is not guaranteed to be unique. */
  layerPathHash?: string
}

type CanvasSelectionContextValue = {
  selection: CanvasSelection | null
  setSelection: (selection: CanvasSelection | null) => void
}

/**
 * Nothing selected, and selecting does nothing.
 *
 * The context carries this rather than `undefined` so consumers can read it
 * unconditionally - it is threaded through the overlays, the inspector and the
 * copy-link shortcut, and every one of them was carrying its own `?.` and
 * `?? noop` for a case none of them can do anything useful about.
 *
 * A no-op rather than a throw because a canvas can legitimately render before
 * anything can be selected on it. What must not happen is a canvas that lets
 * you select things mounting without a provider - then selection silently does
 * nothing - so every view that mounts `FrameInspectorProvider` or the entity
 * overlays mounts `CanvasSelectionProvider` above it.
 */
const NO_SELECTION: CanvasSelectionContextValue = {
  selection: null,
  setSelection: noop,
}

const CanvasSelectionContext =
  createContext<CanvasSelectionContextValue>(NO_SELECTION)

type CanvasSelectionProviderProps = {
  children: React.ReactNode
}

/**
 * The canvas's selection, shared by everything that reads or writes it: the
 * entity overlays, the inspector, the layer tree and the copy-link shortcut.
 *
 * One store rather than two. The inspector used to keep its own
 * `selectedElement` alongside this one and sync between them, which meant a
 * selection made in one mode could not be seen by the other - selecting a frame
 * and then opening the Inspect tab lost it - and that each side had to know how
 * to write to the other's.
 */
export function CanvasSelectionProvider({
  children,
}: CanvasSelectionProviderProps) {
  const [selection, setSelection] = useState<CanvasSelection | null>(null)

  const value = useMemo(
    () => ({
      selection,
      setSelection,
    }),
    [selection]
  )

  return (
    <CanvasSelectionContext.Provider value={value}>
      {children}
    </CanvasSelectionContext.Provider>
  )
}

/**
 * The canvas selection. Always defined: outside a provider it reads as nothing
 * selected, and selecting is a no-op.
 *
 * Selecting is done in elements, or - for the overlays, which have none - in
 * entity ids. Reading gives back identity: the entity id and kind for the
 * consumers that route and copy links, and the raw selection for the inspector,
 * which turns it into an element against the tree it holds.
 *
 * One hook rather than a getter and a setter: both would subscribe to this same
 * context, whose value changes whenever the selection does, so splitting them
 * would not have saved a single render.
 */
export function useCanvasSelection() {
  const { selection, setSelection } = useContext(CanvasSelectionContext)

  const selectElement = useCallback(
    (element: SketchElement | null) => {
      // Selecting the page is not allowed and clears the selection instead.
      // The guard lives here so it holds for every writer rather than only the
      // one that happens to own the state.
      const selectable = element && !isPageElement(element) ? element : null

      if (!selectable) {
        setSelection(null)
        return
      }

      const kind = toSelectionKind(selectable)

      setSelection({
        kind,
        uuid: selectable.elementUUID,
        // Only a layer needs it, and only a layer's uuid is ambiguous without
        // it. Storing it for entities too would suggest it meant something
        // there.
        ...(kind === 'layer'
          ? { layerPathHash: selectable.layerPathHash }
          : {}),
      })
    },
    [setSelection]
  )

  /**
   * Select an entity known only by id, for callers with no scene element to
   * hand - the entity overlays, which work off the renderer's file lists and
   * exist whether or not the inspector has built a tree.
   *
   * The kind comes in because the caller has it: the overlays hold the node and
   * already ask it. Nothing downstream can recover it from the id alone without
   * going back to the file's Section list.
   */
  const selectEntityByUUID = useCallback(
    (uuid: string, kind: CanvasEntityKind) => setSelection({ kind, uuid }),
    [setSelection]
  )

  /**
   * Select a layer known only by its ids, for callers with no scene element to
   * hand - the changes list of a version comparison, which is built from the
   * presentation files rather than from the inspector's tree, and which can be
   * clicked while the Inspect tab has never been opened.
   *
   * Both ids come in because a layer needs both: `uuid` is what the layer is,
   * and `layerPathHash` is which instance of it - see `CanvasSelection`.
   */
  const selectLayerByPathHash = useCallback(
    (uuid: string, layerPathHash: string) =>
      setSelection({ kind: 'layer', uuid, layerPathHash }),
    [setSelection]
  )

  // Narrowed to the entity kinds, so `selectedEntityKind` reads as
  // `CanvasEntityKind | null` rather than leaking 'layer' to callers that have
  // already been told the selection is an entity.
  const selectedEntity =
    selection && selection.kind !== 'layer'
      ? { uuid: selection.uuid, kind: selection.kind }
      : null

  return {
    /**
     * The selection as stored. For the inspector, which resolves it into an
     * element against its tree - see `findSelectedSketchElement`. Everyone else
     * wants one of the two fields below.
     */
    selection,
    /**
     * The id of the selected *entity* - a top-level frame or Section - and null
     * when what is selected is a layer inside one.
     *
     * The distinction matters to everything that treats the selection as a
     * navigable thing: the overlays draw entity chrome, and the copy-link
     * shortcut builds a frame or Section URL from this id. Handing either a
     * layer's id would give them a layer dressed as an entity.
     */
    selectedEntityUUID: selectedEntity?.uuid ?? null,
    /**
     * Whether that entity is a frame or a Section, and null whenever
     * `selectedEntityUUID` is - the two answer for the same selection.
     */
    selectedEntityKind: selectedEntity?.kind ?? null,
    selectElement,
    selectEntityByUUID,
    selectLayerByPathHash,
  }
}

function toSelectionKind(element: SketchElement): CanvasSelectionKind {
  if (!isCanvasEntityElement(element)) return 'layer'

  return isSectionElement(element) ? 'section' : 'frame'
}

/**
 * The selection as an element of the given tree, or null if it is not in it.
 *
 * Resolved on demand rather than stored, so the element always belongs to the
 * tree being shown. The tree is rebuilt whole every time a fragment loads, and
 * a stored element would go on pointing into the previous one - a dead `parent`
 * chain for the layer list to expand, stale styles in the properties panel, and
 * a `prMarinaNode` the renderer may have disposed.
 *
 * A layer is looked up by `layerPathHash` because its uuid is not unique inside
 * symbol instances; an entity by uuid, which is.
 */
export function findSelectedSketchElement(
  sketchSceneRootElement: SketchElement | null,
  selection: CanvasSelection | null
): SketchElement | null {
  if (!selection || !sketchSceneRootElement) return null

  if (selection.kind === 'layer') {
    if (!selection.layerPathHash) return null

    return findSketchElementFromLayerPathHash(
      sketchSceneRootElement,
      selection.layerPathHash
    )
  }

  return findSketchElementFromUuid(sketchSceneRootElement, selection.uuid)
}
